"use client";

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import type { FormikErrors } from "formik";
import { MdEmail } from "react-icons/md";
import { useAppData } from "@/store/appStore";
import { getOtp, getUserData } from "@/app/product/_actions/user.actions";
import { clientLogger } from "@/lib/logger";
import { validateEmail } from "@/lib/utils";
import { Logo } from "@/design-elements/logo";
import { useGoogleIdToken } from "@/hooks/useGoogleIdToken";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";
import { useNativeSSOResult } from "@/hooks/useNativeSSOResult";
import { buildNativeSSOUrl } from "@/lib/utils/native-sso";
import {
	fetchConsentDocument,
	submitConsentAcceptance,
} from "@/lib/client/consent";
import { verifyOAuthIdToken } from "@/app/product/_actions/oauth.actions";
import { getClearedProductListFilters } from "@/lib/utils/product-list-filters";
import { syncUserContextFromCookies } from "@/lib/client/faro-manager";
import {
	getOrCreateDeviceId as getDeviceId,
	syncDeviceId,
} from "@/lib/utils/device-id";
import {
	loginRequest,
	MSAL_REDIRECT_RESULT_KEY,
	MSAL_REDIRECT_ORIGIN_KEY,
} from "@/configs/msal-config";
import { useMsal } from "@azure/msal-react";
import Image from "next/image";
import { trackEvent, GTMEvent, getUTMFromURL } from "@/lib/utils/gtm-events";
import { Dialog, DialogPanel, DialogTitle } from "@/design-elements/dialog";
import { XIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";

const TERMS_DOC_URL =
	"https://icemint.s3.ap-south-1.amazonaws.com/consent/1762856571183-6kwb5jt-consent.md";
const PRIVACY_DOC_URL =
	"https://icemint.s3.ap-south-1.amazonaws.com/common/privacy-policy.md";

interface StepEmailProps {
	onSubmit: (email: string) => void;
	onSkip: () => void;
	onOAuthSuccess?: () => void;
	initialEmail?: string;
}

interface FormValues {
	email: string;
}

const EMAIL_COPY = {
	heroTitle: "What's your work\nemail?",
	heroSubtitle: "Let's see if your company has any\nexclusive offers lined up",
	label: "Work email id",
	placeholder: "sam@companydomain.com",
	submit: "Continue",
	submitting: "Sending...",
	skip: "Skip",
	consentLabel: "I agree to AutoMint terms and privacy policy",
	consentRequired: "Please accept the terms to continue",
	errorInvalidEmail: "Please enter a valid email address.",
	errorUnexpected: "An unexpected error occurred. Please try again.",
	logSubmit: "Onboarding: Email submitted",
	logSkip: "Onboarding: Email step skipped",
};

const StepEmail: React.FC<StepEmailProps> = ({
	onSubmit,
	onSkip,
	onOAuthSuccess,
	initialEmail = "",
}) => {
	const [appData, setAppData, setCookielessData] = useAppData();
	const { instance } = useMsal();
	const [oauthError, setOauthError] = useState<string | null>(null);
	const [isOAuthLoading, setIsOAuthLoading] = useState(false);
	const [consentChecked, setConsentChecked] = useState(true);
	const [modalDoc, setModalDoc] = useState<"terms" | "privacy" | null>(null);
	const [termsMarkdown, setTermsMarkdown] = useState<string | null>(null);
	const [termsLoading, setTermsLoading] = useState(false);
	const [termsError, setTermsError] = useState<string | null>(null);
	const [privacyMarkdown, setPrivacyMarkdown] = useState<string | null>(null);
	const [privacyLoading, setPrivacyLoading] = useState(false);
	const [privacyError, setPrivacyError] = useState<string | null>(null);

	const consentBackendEnabled = appData.isOnboardingConsentEnabled !== false;

	const loadTermsMarkdown = () => {
		if (termsMarkdown || termsLoading) return;
		setTermsLoading(true);
		setTermsError(null);
		fetch(TERMS_DOC_URL)
			.then((res) => {
				if (!res.ok) throw new Error("Failed to load terms");
				return res.text();
			})
			.then((text) => setTermsMarkdown(text))
			.catch((err) => {
				setTermsError(err.message);
				clientLogger.error("Onboarding: Failed to fetch terms doc", err);
			})
			.finally(() => setTermsLoading(false));
	};

	const openTermsDoc = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setModalDoc("terms");
		loadTermsMarkdown();
	};

	const loadPrivacyMarkdown = () => {
		if (privacyMarkdown || privacyLoading) return;
		setPrivacyLoading(true);
		setPrivacyError(null);
		fetch(PRIVACY_DOC_URL)
			.then((res) => {
				if (!res.ok) throw new Error("Failed to load privacy policy");
				return res.text();
			})
			.then((text) => setPrivacyMarkdown(text))
			.catch((err) => {
				setPrivacyError(err.message);
				clientLogger.error("Onboarding: Failed to fetch privacy doc", err);
			})
			.finally(() => setPrivacyLoading(false));
	};

	const openPrivacyDoc = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setModalDoc("privacy");
		loadPrivacyMarkdown();
	};

	const closeModal = () => setModalDoc(null);

	// Persist consent post-login: fetch the per-company consent document with
	// the now-authenticated session, then submit acceptance. Called only after
	// login completes (SSO success handler); the OTP path defers this to
	// step-otp where login() runs.
	const persistConsent = async () => {
		if (!consentBackendEnabled || !consentChecked) return;
		if (appData.hasAcceptedOnBoardingConsent) return;
		try {
			const doc = await fetchConsentDocument("onboarding");
			if (!doc?.id) return;
			const result = await submitConsentAcceptance(doc.id);
			if (result.success) {
				trackEvent(GTMEvent.CONSENT_ACCEPTED, {
					page_path: "Onboarding page",
				});
				setAppData({ hasAcceptedOnBoardingConsent: true });
			}
		} catch (err) {
			clientLogger.error(
				"Onboarding: Failed to persist consent on email step",
				err as Error,
			);
		}
	};

	const { isNativeApp, canUseNativeSSO } = useIsNativeApp();
	const showSSO = !isNativeApp || canUseNativeSSO;

	const {
		login: loginWithGoogle,
		isLoading: isGoogleLoading,
		isReady: isGoogleReady,
	} = useGoogleIdToken({
		onSuccess: (idToken: string) => {
			void handleOAuthLoginSuccess(idToken, "google");
		},
		onError: (error) => console.error("Google Login Failed", error),
	});

	useNativeSSOResult(({ provider, token }) => {
		void handleOAuthLoginSuccess(token, provider);
	});

	const formik = useFormik<FormValues>({
		initialValues: { email: initialEmail },
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			if (values.email && !validateEmail(values.email)) {
				errors.email = EMAIL_COPY.errorInvalidEmail;
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting }) => {
			try {
				const response = await getOtp(values.email, "email");
				if (!response?.error) {
					setAppData({ email: values.email });
					const existingUser = !!response.data?.isExistingUser;
					setCookielessData({ isExistingUser: existingUser });
					sessionStorage.setItem("isExistingUser", String(existingUser));
					clientLogger.info(EMAIL_COPY.logSubmit, {
						email: values.email,
						isExistingUser: existingUser,
					});
					onSubmit(values.email);
					formik.setFieldError("email", undefined);
				} else {
					formik.setFieldError("email", response?.errorText as string);
					setSubmitting(false);
				}
			} catch (err) {
				formik.setFieldError(
					"email",
					err instanceof Error ? err.message : EMAIL_COPY.errorUnexpected,
				);
				setSubmitting(false);
			}
		},
	});

	const handleSkip = () => {
		onSkip();
		trackEvent(GTMEvent.EMAIL_VERIFICATION_SKIPPED);
		clientLogger.info(EMAIL_COPY.logSkip);
	};

	const handleOAuthLoginSuccess = async (
		credential: string,
		provider: "google" | "microsoft",
	) => {
		clientLogger.info(`Onboarding: ${provider} OAuth Login Started`);
		setIsOAuthLoading(true);
		setOauthError(null);

		try {
			const result = await verifyOAuthIdToken(credential, provider);
			if (result.success) {
				try {
					const userData = await getUserData();
					if (userData) {
						const isExistingUser = !!result.data?.isExistingUser;
						setCookielessData({
							user: userData,
							isUserReady: true,
							isExistingUser: isExistingUser,
							...getClearedProductListFilters(true),
						});
						sessionStorage.setItem("isExistingUser", String(isExistingUser));

						trackEvent(isExistingUser ? GTMEvent.LOGIN : GTMEvent.SIGNUP, {
							auth_method: "sso",
							page_path: "Onboarding page",
							...getUTMFromURL(),
						});

						const deviceId = getDeviceId();
						if (deviceId) syncDeviceId(deviceId);

						try {
							syncUserContextFromCookies();
						} catch (error) {
							console.error("Error syncing context", error);
						}

						const email = userData?.email;
						if (email) {
							const appUpdates: Record<string, unknown> = {
								email: email,
								hasAcceptedOnBoardingConsent: userData.hasAcceptedConsent,
							};
							if (userData.employment?.annualCtc) {
								appUpdates.ctc = userData.employment.annualCtc;
							}
							if (userData.firstName) {
								appUpdates.firstName = userData.firstName;
							}
							if (userData.mobileNo) {
								appUpdates.phoneNumber = userData.mobileNo;
							}
							setAppData(appUpdates);

							clientLogger.info(`Onboarding: ${provider} OAuth Login Success`, {
								email,
								isExistingUser,
							});

							await persistConsent();

							if (onOAuthSuccess) {
								onOAuthSuccess();
							} else {
								onSubmit(email);
							}
						} else {
							setOauthError(
								`Unable to retrieve email from ${provider} account.`,
							);
						}
					}
				} catch (getUserError) {
					console.error("[step-email] getUserData() failed:", getUserError);
					setOauthError("Failed to fetch user data after OAuth.");
				}
			} else {
				setOauthError(result.errorText || `${provider} Login Failed.`);
			}
		} catch (e) {
			const errorMessage =
				e instanceof Error ? e.message : `${provider} Login connection failed.`;
			clientLogger.error(
				`Onboarding: ${provider} OAuth Error`,
				e instanceof Error ? e : new Error(String(e)),
			);
			setOauthError(errorMessage);
		} finally {
			setIsOAuthLoading(false);
		}
	};

	useEffect(() => {
		const stored = sessionStorage.getItem(MSAL_REDIRECT_RESULT_KEY);
		if (stored) {
			sessionStorage.removeItem(MSAL_REDIRECT_RESULT_KEY);
			try {
				const { idToken } = JSON.parse(stored);
				void handleOAuthLoginSuccess(idToken, "microsoft");
			} catch (e) {
				console.error("Failed to parse MSAL redirect result", e);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const emailError = formik.touched.email ? formik.errors.email : undefined;
	const consentBlocks = !consentChecked;
	const continueInFlight = formik.isSubmitting || isOAuthLoading;
	const continueDisabled =
		continueInFlight ||
		!!emailError ||
		!formik.values.email ||
		!validateEmail(formik.values.email) ||
		consentBlocks;
	const showContinueDisabledStyle = continueDisabled && !continueInFlight;
	const ssoDisabled = isOAuthLoading || isGoogleLoading || consentBlocks;
	const googleSsoDisabled = ssoDisabled || (!canUseNativeSSO && !isGoogleReady);

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-8 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px] leading-tight whitespace-pre-line">
							{EMAIL_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3 whitespace-pre-line">
							{EMAIL_COPY.heroSubtitle}
						</p>
					</div>

					<div className="mt-6">
						<label
							htmlFor="onboarding_email_verification_input"
							className="block text-sm text-[var(--automint-black)] md:text-white mb-2"
						>
							{EMAIL_COPY.label}
						</label>
						<div className="relative">
							<MdEmail
								className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]"
								size={18}
							/>
							<input
								id="onboarding_email_verification_input"
								name="email"
								type="email"
								autoComplete="email"
								autoFocus
								placeholder={EMAIL_COPY.placeholder}
								value={formik.values.email}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								disabled={formik.isSubmitting}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !continueDisabled) {
										formik.handleSubmit();
									}
								}}
								className="w-full h-12 rounded-[6px] bg-[var(--colors-automint-neutral-200)] border border-[var(--colors-automint-neutral-200)] text-[var(--automint-black)] placeholder:text-[var(--colors-automint-neutral-500)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] md:text-white md:placeholder:text-[var(--automint-onboarding-placeholder-dark)] pl-11 pr-4 text-[15px] focus:outline-none focus:border-[var(--automint-black)] md:focus:border-[var(--automint-onboarding-focus-border-dark)] disabled:opacity-60"
							/>
						</div>
						{emailError && (
							<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-xs mt-2">
								{emailError}
							</p>
						)}
					</div>

					<label
						htmlFor="onboarding_email_consent"
						className="mt-4 flex items-start gap-2 text-[13px] text-[var(--automint-black)] md:text-[var(--automint-onboarding-consent-text-dark)] cursor-pointer select-none"
					>
						<input
							id="onboarding_email_consent"
							type="checkbox"
							checked={consentChecked}
							onChange={(e) => setConsentChecked(e.target.checked)}
							className="sr-only peer"
						/>
						<span
							aria-hidden="true"
							className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
								consentChecked
									? "bg-[var(--automint-black)] border-[var(--automint-black)] md:bg-[var(--colors-automint-neon-500)] md:border-[var(--colors-automint-neon-500)]"
									: "bg-white border-[var(--colors-automint-neutral-300)] md:bg-[var(--colors-automint-neutral-800)] md:border-[var(--colors-automint-neutral-800)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--colors-automint-neon-500)]"
							}`}
						>
							{consentChecked && (
								<svg
									width="10"
									height="10"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="text-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)]"
								>
									<path
										d="M2 6.5L4.5 9L10 3"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							)}
						</span>
						<span>
							I agree to{" "}
							<button
								type="button"
								onClick={openTermsDoc}
								className="underline text-[var(--automint-black)] md:text-white cursor-pointer bg-transparent p-0"
							>
								AutoMint terms
							</button>{" "}
							and{" "}
							<button
								type="button"
								onClick={openPrivacyDoc}
								className="underline text-[var(--automint-black)] md:text-white cursor-pointer bg-transparent p-0"
							>
								privacy policy
							</button>
						</span>
					</label>

					<div className="mt-5">
						<button
							id="onboarding_email_verification_submit"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={continueDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showContinueDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting ? EMAIL_COPY.submitting : EMAIL_COPY.submit}
						</button>
					</div>

					{showSSO && (
						<div className="flex items-center my-5">
							<div className="flex-grow border-t border-[var(--colors-automint-neutral-300)] md:border-[var(--automint-onboarding-sso-border-dark)]" />
							<span className="mx-3 text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] text-xs">
								Or
							</span>
							<div className="flex-grow border-t border-[var(--colors-automint-neutral-300)] md:border-[var(--automint-onboarding-sso-border-dark)]" />
						</div>
					)}

					{showSSO && (
						<div className="flex flex-col gap-3">
							<button
								type="button"
								onClick={() => {
									if (canUseNativeSSO) {
										window.location.href = buildNativeSSOUrl("google");

										return;
									}
									loginWithGoogle();
								}}
								disabled={googleSsoDisabled}
								className="w-full h-12 rounded-[6px] bg-[var(--automint-onboarding-bg)] border border-[var(--colors-automint-neutral-300)] text-[var(--automint-black)] md:bg-[var(--automint-onboarding-sso-bg-dark)] md:border-[var(--automint-onboarding-sso-border-dark)] md:text-white flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<Image
									src="/new_icons/google/google-icon.svg"
									alt="Google"
									width={18}
									height={18}
								/>
								<span className="text-sm ">
									{isGoogleLoading
										? "Connecting..."
										: "Login with Google Workspace"}
								</span>
							</button>
							<button
								type="button"
								onClick={() => {
									if (canUseNativeSSO) {
										window.location.href = buildNativeSSOUrl("microsoft");

										return;
									}
									sessionStorage.setItem(
										MSAL_REDIRECT_ORIGIN_KEY,
										window.location.href,
									);
									instance.loginRedirect(loginRequest).catch((e) => {
										console.error("Microsoft Login Failed", e);
									});
								}}
								disabled={ssoDisabled}
								className="w-full h-12 rounded-[6px] bg-[var(--automint-onboarding-bg)] border border-[var(--colors-automint-neutral-300)] text-[var(--automint-black)] md:bg-[var(--automint-onboarding-sso-bg-dark)] md:border-[var(--automint-onboarding-sso-border-dark)] md:text-white flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								<Image
									src="/new_icons/microsoft/microsoft-icon.svg"
									alt="Microsoft"
									width={18}
									height={18}
								/>
								<span className="text-sm ">Login with Microsoft Workspace</span>
							</button>
						</div>
					)}

					{oauthError && (
						<p className="text-[var(--automint-error-text-light)] md:text-[var(--automint-error-text-dark)] text-sm mt-3 text-center">
							{oauthError}
						</p>
					)}

					<div className="mt-4 mb-2 text-center">
						<button
							id="onboarding_email_verification_skip"
							type="button"
							onClick={handleSkip}
							className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
						>
							Skip
						</button>
					</div>
				</div>
			</div>

			<Dialog
				open={modalDoc !== null}
				onClose={closeModal}
				className="!z-[200]"
			>
				<DialogPanel className="max-w-lg">
					<div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-gray-200">
						<DialogTitle>
							{modalDoc === "privacy" ? "Privacy Policy" : "Terms & Conditions"}
						</DialogTitle>
						<button
							type="button"
							onClick={closeModal}
							aria-label="Close"
							className="text-gray-500 hover:text-gray-900"
						>
							<XIcon className="h-5 w-5" />
						</button>
					</div>
					<div className="px-5 py-4 max-h-[60vh] overflow-y-auto text-left text-sm text-gray-700 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_li]:my-1 [&_strong]:font-semibold [&_a]:text-blue-600 [&_a]:underline [&_hr]:my-3 [&_hr]:border-gray-200">
						{modalDoc === "privacy" ? (
							privacyLoading ? (
								<p className="text-sm text-gray-500 text-center">Loading...</p>
							) : privacyError ? (
								<p className="text-sm text-red-600 text-center">
									{privacyError}
								</p>
							) : privacyMarkdown ? (
								<ReactMarkdown>{privacyMarkdown}</ReactMarkdown>
							) : (
								<p className="text-sm text-gray-500 text-center">
									Document is not available. Please try again later.
								</p>
							)
						) : termsLoading ? (
							<p className="text-sm text-gray-500 text-center">Loading...</p>
						) : termsError ? (
							<p className="text-sm text-red-600 text-center">{termsError}</p>
						) : termsMarkdown ? (
							<ReactMarkdown>
								{termsMarkdown
									.replaceAll("{{email}}", formik.values.email || "your email")
									.replaceAll("{{company}}", "your employer")}
							</ReactMarkdown>
						) : (
							<p className="text-sm text-gray-500 text-center">
								Document is not available. Please try again later.
							</p>
						)}
					</div>
				</DialogPanel>
			</Dialog>
		</section>
	);
};

export default StepEmail;
